/* eslint-disable no-console */
import { useState, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { PromptService } from '../features/prompt-debugger/services/promptService';
import {
  PromptTestResponse,
  ModelConfig,
  PromptConfig,
} from '../features/prompt-debugger/types';

interface UsePromptDebugState {
  loading: boolean;
  testResponse: PromptTestResponse | null;
  promptConfig: PromptConfig | null;
  modelConfig: ModelConfig | null;
}

export const usePromptDebug = (promptId: string) => {
  const notify = useNotify();
  const [state, setState] = useState<UsePromptDebugState>({
    loading: false,
    testResponse: null,
    promptConfig: null,
    modelConfig: null,
  });

  // Test a prompt
  const testPrompt = useCallback(
    async (
      content: string,
      modelName: string,
      parameters: Record<string, unknown>
    ) => {
      setState(prev => ({ ...prev, loading: true }));

      try {
        const response = await PromptService.testPrompt(
          promptId,
          content,
          modelName,
          parameters,
          notify
        );

        // Log the raw test response as received by the hook before UI parsing
        try {
          console.log(
            '🧪 usePromptDebug.testPrompt raw PromptTestResponse:',
            response
          );
          if (
            response &&
            response.success &&
            typeof response.result === 'string'
          ) {
            console.log(
              '🧪 usePromptDebug.testPrompt result (string) length:',
              response.result.length
            );
            console.log(
              '🧪 usePromptDebug.testPrompt result (string) preview:',
              response.result.slice(0, 500)
            );
          }
        } catch {
          // Ignore error in debug logging
        }

        setState(prev => ({ ...prev, testResponse: response, loading: false }));

        if (response.success) {
          notify('Prompt executed successfully', { type: 'success' });
        } else {
          notify(`Prompt execution failed: ${response.error}`, {
            type: 'error',
          });
        }

        return response;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        notify(`Error executing prompt: ${errorMessage}`, { type: 'error' });
        setState(prev => ({ ...prev, loading: false }));
        return null;
      }
    },
    [promptId, notify]
  );

  // Load prompt configuration
  const loadPromptConfig = useCallback(async () => {
    try {
      const config = await PromptService.getPromptConfig(promptId);
      setState(prev => ({ ...prev, promptConfig: config }));
      return config;
    } catch {
      notify('Failed to load prompt configuration', { type: 'error' });
      return null;
    }
  }, [promptId, notify]);

  // Load prompt content string
  const loadPromptContent = useCallback(async () => {
    try {
      const content = await PromptService.getPromptContent(promptId);
      return content;
    } catch {
      notify('Failed to load prompt content', { type: 'error' });
      return null;
    }
  }, [promptId, notify]);

  // Save prompt configuration
  const savePromptConfig = useCallback(
    async (config: PromptConfig) => {
      try {
        const success = await PromptService.savePromptConfig(promptId, config);
        if (success) {
          setState(prev => ({ ...prev, promptConfig: config }));
          notify('Prompt configuration saved', { type: 'success' });
        } else {
          notify('Failed to save prompt configuration', { type: 'error' });
        }
        return success;
      } catch {
        notify('Error saving prompt configuration', { type: 'error' });
        return false;
      }
    },
    [promptId, notify]
  );

  // Load model configuration
  const loadModelConfig = useCallback(async () => {
    try {
      const config = await PromptService.getModelConfig(promptId);
      setState(prev => ({ ...prev, modelConfig: config }));
      return config;
    } catch {
      notify('Failed to load model configuration', { type: 'error' });
      return null;
    }
  }, [promptId, notify]);

  // Save model configuration
  const saveModelConfig = useCallback(
    async (config: ModelConfig) => {
      try {
        const success = await PromptService.saveModelConfig(promptId, config);
        if (success) {
          setState(prev => ({ ...prev, modelConfig: config }));
          notify('Model configuration saved', { type: 'success' });
        } else {
          notify('Failed to save model configuration', { type: 'error' });
        }
        return success;
      } catch {
        notify('Error saving model configuration', { type: 'error' });
        return false;
      }
    },
    [promptId, notify]
  );

  // Improve prompt using AI
  const improvePrompt = useCallback(
    async (currentPrompt: string, instructions: string, context: string) => {
      setState(prev => ({ ...prev, loading: true }));

      try {
        const result = await PromptService.improvePrompt(
          currentPrompt,
          instructions,
          context
        );

        setState(prev => ({ ...prev, loading: false }));

        if (result) {
          notify('Prompt improved successfully', { type: 'success' });
          return result;
        } else {
          notify('Failed to improve prompt', { type: 'error' });
          return null;
        }
      } catch {
        setState(prev => ({ ...prev, loading: false }));
        notify('Error improving prompt', { type: 'error' });
        return null;
      }
    },
    [notify]
  );

  return {
    ...state,
    testPrompt,
    loadPromptConfig,
    loadPromptContent,
    savePromptConfig,
    loadModelConfig,
    saveModelConfig,
    improvePrompt,
  };
};
